import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PlacementAnalyticsService from './PlacementAnalyticsService';
import { directoryByCompanyPath, isDirectoryLinkEnabled, type PlacementRoleBase } from './deepLinks';
import {
    Bubble,
    QUADRANT_COLORS,
    SPM_QUAD_META,
    buildBubbleData,
    matrixOptions,
    MatrixHoverCard,
    hoverCardStyle,
    splitOutliers,
    plotYDomainMax,
    OutlierLane,
    YOutlierLane,
} from './components/matrixChartHelpers';
import './placementAnalytics.css';

interface Props {
    instituteId: number | string;
    roleBase: PlacementRoleBase;
}

const SPM_ORDER = SPM_QUAD_META.map((q) => q.key);

const PlacementSpmMatrix = ({ instituteId, roleBase }: Props) => {
    const [programId, setProgramId] = useState<number | null>(null);
    const [data, setData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedQuadrant, setSelectedQuadrant] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [hover, setHover] = useState<{
        bubble: any;
        x: number;
        y: number;
        chartW: number;
        chartH: number;
    } | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        PlacementAnalyticsService.getSpmMatrix(instituteId, programId)
            .then((res) => {
                if (!cancelled) {
                    setData(res.data);
                    setSelectedQuadrant(null);
                }
            })
            .catch((e) => {
                if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load SPM');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [instituteId, programId]);

    const toggleQuadrant = (q: string | null) => {
        setSelectedQuadrant((cur) => (q != null && cur === q ? null : q));
    };

    const chartRowRef = useRef<HTMLDivElement>(null);
    const bubbles = data?.bubbles || [];
    const axis = data?.axis || {};
    const { main: plotBubbles, xOutliers: xOutlierBubbles, yOutliers: yOutlierBubbles } = useMemo(
        () => splitOutliers(bubbles),
        [bubbles]
    );

    const matchedCompanyIds = useMemo<Set<string | number> | null>(() => {
        if (!search.trim()) return null;
        const q = search.trim().toLowerCase();
        return new Set<string | number>(
            bubbles
                .filter((b: any) => String(b.companyName || '').toLowerCase().includes(q))
                .map((b: any) => b.companyId)
        );
    }, [bubbles, search]);
    const chartData = useMemo(
        () => buildBubbleData(plotBubbles, selectedQuadrant, 'x', 'y', matchedCompanyIds),
        [plotBubbles, selectedQuadrant, matchedCompanyIds]
    );
    const xMax = Number(axis.xMax ?? 0);
    const yDomainMax = useMemo(
        () => plotYDomainMax(bubbles, Number(axis.yMax ?? 0)),
        [bubbles, axis.yMax]
    );
    const options = useMemo(
        () =>
            matrixOptions({
                xLabel: 'Offers (log)',
                yLabel: 'Mean Salary (LPA)',
                xThreshold: Number(axis.xThreshold ?? 5),
                xMax,
                yThreshold: Number(axis.yThreshold ?? 13),
                yMax: yDomainMax,
                logX: true,
                selectedQuadrant,
                quadrantMeta: SPM_QUAD_META,
                onHoverPoint: setHover,
                onQuadrantSelect: toggleQuadrant,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [axis, selectedQuadrant, yDomainMax]
    );

    const list = useMemo(() => {
        let rows = bubbles;
        if (selectedQuadrant) rows = rows.filter((b: any) => b.quadrant === selectedQuadrant);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            rows = rows.filter((b: any) => String(b.companyName || '').toLowerCase().includes(q));
        }
        return rows;
    }, [bubbles, selectedQuadrant, search]);

    if (loading) return <div className="text-muted font-13">Loading SPM matrix…</div>;
    if (error) return <div className="alert alert-danger py-2 font-13">{error}</div>;
    if (!data?.uploadId) return <div className="alert alert-info py-2 font-13">No ANALYSIS data uploaded.</div>;

    const counts = data.quadrantCounts || {};
    const yMax = Number(axis.yMax ?? 0);
    const programLabel =
        (data.programs || []).find((p: any) => {
            const id = p.id == null || p.isAllPrograms ? null : Number(p.id ?? p.placementProgramId);
            return programId == null ? id == null : id === programId;
        })?.programDisplayName || 'All Programs';

    return (
        <div>
            <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                {(data.programs || []).map((p: any) => {
                    const id = p.id == null || p.isAllPrograms ? null : Number(p.id ?? p.placementProgramId);
                    const active = programId == null ? id == null : id === programId;
                    return (
                        <button
                            key={String(id)}
                            type="button"
                            className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setProgramId(id)}>
                            {p.programDisplayName || 'All Programs'}
                        </button>
                    );
                })}
            </div>

            <div className="mx-filter-row mb-3">
                <span className="mx-filter-label">Filter:</span>
                <button
                    type="button"
                    className={`mx-filter-pill ${selectedQuadrant == null ? 'active' : ''}`}
                    onClick={() => toggleQuadrant(null)}>
                    All
                </button>
                {SPM_ORDER.map((q) => (
                    <button
                        key={q}
                        type="button"
                        className={`mx-filter-pill ${selectedQuadrant === q ? 'active' : ''}`}
                        style={
                            selectedQuadrant === q
                                ? { background: QUADRANT_COLORS[q], borderColor: QUADRANT_COLORS[q] }
                                : { borderColor: QUADRANT_COLORS[q], color: QUADRANT_COLORS[q] }
                        }
                        onClick={() => toggleQuadrant(q)}>
                        {SPM_QUAD_META.find((m) => m.key === q)?.label || q}
                    </button>
                ))}
            </div>

            <div className="row g-3">
                <div className="col-lg-8">
                    <div style={{ height: 440 }} className="border rounded p-2 mx-chart">
                        <div className="mx-chart-inner" ref={chartRowRef}>
                            <div className="mx-chart-col">
                                {yOutlierBubbles.length > 0 && (
                                    <div className="mx-outlier-lane-top-row">
                                        <YOutlierLane
                                            bubbles={yOutlierBubbles}
                                            height={36}
                                            xMax={xMax}
                                            xMin={1}
                                            logX
                                            onHoverPoint={setHover}
                                            containerRef={chartRowRef}
                                        />
                                        {xOutlierBubbles.length > 0 && (
                                            <div className="mx-outlier-lane-top-spacer" />
                                        )}
                                    </div>
                                )}
                                <div className="mx-chart-row">
                                    <div className="mx-chart-plot">
                                        <Bubble data={chartData as any} options={options} />
                                    </div>
                                    {xOutlierBubbles.length > 0 && (
                                        <OutlierLane
                                            bubbles={xOutlierBubbles}
                                            height={yOutlierBubbles.length > 0 ? 372 : 408}
                                            yMax={yDomainMax}
                                            onHoverPoint={setHover}
                                            containerRef={chartRowRef}
                                        />
                                    )}
                                </div>
                            </div>
                            {hover && (
                                <div className="mx-hover-wrap" style={hoverCardStyle(hover)}>
                                    <MatrixHoverCard bubble={hover.bubble} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="mx-counts-head">Quadrant Counts · {programLabel}</div>
                    <div className="mx-counts-grid">
                        {SPM_ORDER.map((q) => (
                            <button
                                key={q}
                                type="button"
                                className={`mx-count-card ${selectedQuadrant === q ? 'active' : ''}`}
                                style={{ borderLeftColor: QUADRANT_COLORS[q] }}
                                onClick={() => toggleQuadrant(q)}>
                                <div className="mx-count-value" style={{ color: QUADRANT_COLORS[q] }}>
                                    {counts[q] ?? 0}
                                </div>
                                <div className="mx-count-label">
                                    {SPM_QUAD_META.find((m) => m.key === q)?.label || q}
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="mx-list-head mt-3">Company List</div>
                    <div className="mx-filter-row mb-2">
                        <button
                            type="button"
                            className={`mx-filter-pill sm ${selectedQuadrant == null ? 'active' : ''}`}
                            onClick={() => toggleQuadrant(null)}>
                            All
                        </button>
                        {SPM_ORDER.map((q) => (
                            <button
                                key={`list-${q}`}
                                type="button"
                                className={`mx-filter-pill sm ${selectedQuadrant === q ? 'active' : ''}`}
                                onClick={() => toggleQuadrant(q)}>
                                {q.split(' ')[0]}
                            </button>
                        ))}
                    </div>
                    <input
                        className="form-control form-control-sm mb-2"
                        placeholder="Search companies…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div style={{ maxHeight: 280, overflow: 'auto' }}>
                        {list.map((b: any) => (
                            <div
                                key={b.companyId}
                                className="d-flex align-items-center gap-2 py-1 border-bottom font-13"
                                onMouseEnter={() =>
                                    setHover({
                                        bubble: b,
                                        x: 24,
                                        y: 24,
                                        chartW: 400,
                                        chartH: 400,
                                    })
                                }
                                onMouseLeave={() => setHover(null)}>
                                <span
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: QUADRANT_COLORS[b.quadrant] || '#999',
                                    }}
                                />
                                {isDirectoryLinkEnabled(roleBase) ? (
                                    <Link
                                        to={directoryByCompanyPath(b.companyId)}
                                        className="flex-grow-1 text-decoration-none">
                                        {b.companyName}
                                    </Link>
                                ) : (
                                    <span className="flex-grow-1">{b.companyName}</span>
                                )}
                                <span className="text-muted">{b.y ?? b.meanSalaryLpa ?? '—'}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlacementSpmMatrix;
